import { AuthProvider as Gen2AuthProvider, useAuth } from '@steelhouse/auth-client-react'
import type React from 'react'
import { createContext, useCallback, useContext } from 'react'

export const AuthProvider = ({
  children,
  passBaseUrl,
  apiBaseUrl,
  environment,
  selectApiBaseUrl,
}: {
  children: React.ReactNode
  passBaseUrl: string
  apiBaseUrl: string
  selectApiBaseUrl?: string
  environment?: string
}) => {
  const normalizedPassBaseUrl = passBaseUrl.replace(/\/+$/, '')

  return (
    <Gen2AuthProvider
      config={{
        apiBaseUrl,
        passBaseUrl: normalizedPassBaseUrl,
        signInUrl: `${normalizedPassBaseUrl}/sign-in`,
        signUpUrl: `${normalizedPassBaseUrl}/sign-up`,
        environment,
      }}
    >
      <AuthContextProvider selectApiBaseUrl={selectApiBaseUrl} passBaseUrl={normalizedPassBaseUrl}>
        {children}
      </AuthContextProvider>
    </Gen2AuthProvider>
  )
}

type AuthContext = {
  signOut: (args?: { sessionId?: string; redirectUrl?: string }) => void
  redirectToSignIn: (params?: { redirectUrl?: string }) => void
  getToken: (options?: { template?: string }) => Promise<string | null>
  isLoaded: boolean
  isAuthenticated: boolean | undefined
}

const AuthContext = createContext<AuthContext | undefined>(undefined)

const AuthContextProvider = ({
  children,
  selectApiBaseUrl,
  passBaseUrl,
}: {
  children: React.ReactNode
  selectApiBaseUrl?: string
  passBaseUrl: string
}) => {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth()

  const handleRedirectToSignIn = useCallback(
    (params?: { redirectUrl?: string }) => {
      const redirectUrl = params?.redirectUrl || window.location.href

      const signInUrl = new URL(`${passBaseUrl}/sign-in`)
      signInUrl.searchParams.set('redirect_url', redirectUrl)

      window.location.href = signInUrl.toString()
    },
    [passBaseUrl],
  )

  const handleSignOut = useCallback(
    async (args?: { sessionId?: string; redirectUrl?: string }) => {
      const token = await getToken()

      if (selectApiBaseUrl) {
        try {
          /** Sign out deletes PTV cookie */
          await fetch(`${selectApiBaseUrl}/api/seller/v1/me/sign-out`, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: token ? `Bearer ${token}` : '',
            },
            method: 'POST',
            credentials: 'include',
          })
        } catch {
          // ignore and continue to sign out
        }
      }

      signOut(args)
    },
    [getToken, selectApiBaseUrl, signOut],
  )

  return (
    <AuthContext.Provider
      value={{
        isLoaded,
        isAuthenticated: isSignedIn,
        getToken,
        signOut: handleSignOut,
        redirectToSignIn: handleRedirectToSignIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthContext = () => {
  const authContext = useContext(AuthContext)
  if (!authContext) {
    throw new Error('Auth Provider not found')
  }

  return authContext
}
